<template>
  <div>
    <div class="d-flex flex-column-reverse flex-lg-row">
      <b-col
        cols="12"
        lg="8"
      >
        <b-card>
          <h4 class="font-weight-bolder mb-0">
            {{ ticket.subject }}
          </h4>
        </b-card>
        <div
          v-for="(convo, index) in rows"
          :key="index"
        >
          <b-card
            :style="convo.user_id === currentUser.id && !convo.user.merchant_id ? '' : 'filter:brightness(0.9)'"
            class="pr-1 convo-card"
          >
            <div class="d-flex justify-content-between">
              <div class="px-1 d-flex">
                <b-avatar
                  :text="convo.user_id === currentUser.id && !convo.user.merchant_id ? 'M' : avatarText(`${convo.user.first_name || ''} ${convo.user.last_name || ''}`)"
                  size="32"
                  variant="light-primary"
                />
                <div>
                  <div class="font-weight-bolder ml-1 text-primary">
                    {{ convo.user_id === currentUser.id && !convo.user.merchant_id ? 'Me' : `${convo.user.first_name || ''} ${convo.user.last_name || ''}` }}
                  </div>
                  <div
                    v-if="convo.user.merchant_id"
                    class="ml-1 text-secondary font-small-2"
                  >
                    {{ convo.user.defualt_merchant_business.length > 0 ? convo.user.defualt_merchant_business[0].business_name : '' }}
                  </div>
                  <div
                    v-if="(convo.user_id === currentUser.id && !convo.user.merchant_id)"
                    class="font-weight-bolder ml-1 font-small-2"
                  >
                    {{ convo.user.email || '' }}
                  </div>
                  <div class="pt-1 ml-1">
                    {{ convo.message }}
                  </div>
                  <div
                    v-if="convo.attachments && convo.attachments.length > 0"
                    class="ml-1 attachment-section"
                  >
                    <a
                      v-for="(attachmentUrl, attachIndex) in convo.attachments"
                      :key="attachIndex"
                      :href="`${bucketUrl}${attachmentUrl}`"
                      target="_blank"
                      class="attachment-wrapper mt-1"
                    >
                      <div class="attachment-content">
                        <!-- Left Side: Paperclip and Filename -->
                        <div class="d-flex align-items-center overflow-hidden">
                          <feather-icon
                            icon="PaperclipIcon"
                            size="18"
                            class="text-secondary mr-75"
                          />
                          <span class="attachment-name text-truncate">
                            {{ getFileName(attachmentUrl) }}
                            <!-- Note: convo.attachment_size is a single field. If you have multiple attachments,
                                 you'd ideally have an array of sizes or fetch size dynamically.
                                 For now, this will only show the size for the first attachment if
                                 convo.attachment_size is a string, or you'd need convo.attachments[attachIndex].size.
                                 I'm commenting it out as it's probably not per-attachment currently.
                            -->
                            <!-- <span
                              v-if="convo.attachment_size"
                              class="attachment-size"
                            >
                              ({{ convo.attachment_size }})
                            </span> -->
                          </span>
                        </div>

                        <!-- Right Side: Eye Icon -->
                        <feather-icon
                          icon="EyeIcon"
                          size="20"
                          class="eye-icon ml-1"
                        />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
              <span class="font-small-2 text-secondary mb-0 ml-1">
                {{ __dateTimeFormatterPassedTime(convo.created_at) }}
              </span>
            </div>
          </b-card>
          <!-- <b-badge
            variant="light-info"
            class="w-100 ticket-convo-status"
          >
            <div class="font-weight-bolder">
              Ticket Status Changed to "In Progress"
            </div>
          </b-badge> -->
        </div>
        <b-card v-if="ticket.status !== 'closed'">
          <b-overlay
            :show="loading"
            blur="10px"
            opacity="0.30"
            rounded="sm"
            variant="light"
          >
            <b-form @submit.prevent="onSubmit">
              <b-form-group
                label="Message"
                label-for="message"
              >
                <b-form-textarea
                  id="message"
                  v-model="form.message"
                  placeholder="Enter Subject / Reply"
                  rows="6"
                  required
                />
              </b-form-group>
              <b-form-group
                label="Attachments"
                label-for="attachments"
              >
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="PaperclipIcon" />
                  </b-input-group-prepend>
                  <b-form-file
                    id="wildcard"
                    v-model="form.file"
                    accept="image/*"
                  />
                </b-input-group>
              </b-form-group>
              <b-button
                v-can="'edit-tickets'"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                class="w-100"
                type="submit"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  class="mr-50"
                />
                <span class="align-middle">Submit</span>
              </b-button>
            </b-form>
          </b-overlay>
        </b-card>
      </b-col>
      <b-col
        cols="12"
        lg="4"
      >
        <b-badge
          :variant="`light-${ticket.status !== 'closed' ? 'primary' : 'danger'}`"
          class="w-100 ticket-status-badge"
        >
          <div class="ticket-status font-weight-bolder">
            {{ ticket.status !== 'closed' ? 'Ticket Unresolved' : 'Ticket Resolved' }}
          </div>
        </b-badge>
        <b-card>
          <div class="mb-0 font-weight-bolder text-secondary font-small-3">
            Shipment Info
          </div>
          <div class="mt-1 d-flex justify-content-between">
            <div>
              <div class="mb-0 font-weight-bolder text-secondary font-small-2">
                Waybill
              </div>
              <div
                class="font-weight-bolder text-primary font-small-3 cursor-pointer"
                @click="ticket.order && onClickViewOrder(ticket.order.id)"
              >
                {{ ticket.waybill_number || '---' }}
              </div>
            </div>
            <div>
              <div class="mb-0 font-weight-bolder text-secondary font-small-2">
                Order Status
              </div>
              <order-status-badge :status-key="ticket.order ? ticket.order.latest_primary_status_key : null" />
            </div>
          </div>
        </b-card>
        <b-card>
          <div class="mb-0 font-weight-bolder text-secondary font-small-3">
            Reference Number
          </div>

          <div>
            {{ `Ref - ${ticket.id}` }}
          </div>

          <div class="pt-1 mt-1 mb-0 font-weight-bolder text-secondary font-small-3">
            Ticket Status
          </div>

          <b-dropdown
            id="dropdown-status"
            :text="currentStatusText"
            :variant="currentStatusVariant"
            class="ticket-status-dropdown"
          >
            <b-dropdown-item
              v-for="statusOption in statusOptions"
              :key="statusOption.value"
              :active="ticket.status === statusOption.value"
              @click="updateTicketStatus(statusOption.value)"
            >
              <div class="d-flex justify-content-between align-items-center w-100">
                <b-badge :variant="`light-${statusOption.variant}`">
                  {{ statusOption.text }}
                </b-badge>
                <feather-icon
                  v-if="ticket.status === statusOption.value"
                  icon="CheckIcon"
                  size="18"
                  class="text-secondary mx-1"
                />
                <div
                  v-else
                  class="ml-1 mr-2"
                />
              </div>
            </b-dropdown-item>
          </b-dropdown>

          <div class="d-flex justify-content-between">
            <div>
              <div class="pt-1 mt-1 mb-0 font-weight-bolder text-secondary font-small-3">
                Created Date
              </div>

              <div>
                {{ __dateTimeFormatter(ticket.created_at) }}
              </div>
            </div>
            <div>
              <div class="pt-1 mt-1 mb-0 font-weight-bolder text-secondary font-small-3">
                Last Updated
              </div>

              <div>
                {{ __dateTimeFormatterPassedTime(ticket.updated_at) }}
              </div>
            </div>
          </div>
        </b-card>
        <b-card>
          <div class="d-flex justify-content-between">
            <div>
              <div class="mb-0 font-weight-bolder text-secondary font-small-3">
                Added By
              </div>
              <div>
                {{ ticket.created_by_staff ? `${ticket.created_by_staff.first_name || ''} ${ticket.created_by_staff.last_name || ''}` : (ticket.created_by ? `${ticket.created_by.first_name || ''} ${ticket.created_by.last_name || ''}` : '---') }}
              </div>
            </div>
            <div>
              <div class="mb-0 font-weight-bolder text-secondary font-small-3">
                Reported By
              </div>
              <div>
                {{ ticket.reported_by ? (ticket.reported_by.charAt(0).toUpperCase() + ticket.reported_by.slice(1)) : '---' }}
              </div>
            </div>
          </div>
          <div class="pt-1 mt-1 mb-0 font-weight-bolder text-secondary font-small-3">
            Assignee
          </div>
          <div>
            {{ (ticket.assigned_by ? `${ticket.assigned_by.first_name || ''} ${ticket.assigned_by.last_name || ''}` : '---') }}
          </div>
        </b-card>

        <b-card v-if="ticket.status !== 'closed'">
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="danger"
            class="w-100 p-1"
            size="sm"
            @click="openCloseTicket(true)"
          >
            <feather-icon
              icon="XCircleIcon"
            />
            {{ 'Close Ticket' }}
          </b-button>
        </b-card>
      </b-col>
    </div>
    <view-order-modal ref="viewOrderModal" />
  </div>
</template>

<script>
import {
  BAvatar,
  BButton,
  BCard,
  BCol,
  BInputGroup,
  BInputGroupPrepend,
  BForm,
  BFormGroup,
  BFormTextarea,
  BFormFile,
  BOverlay,
  VBModal,
  BBadge,
  VBTooltip,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { bucketUrl } from '@/constants/config'
import { avatarText } from '@core/utils/filter'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const TicketRepository = RepositoryFactory.get('ticket')
const StaffAuthRepository = RepositoryFactory.get('staffauth')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BAvatar,
    BButton,
    BInputGroup,
    BInputGroupPrepend,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormFile,
    BCard,
    BCol,
    BOverlay,
    BBadge,
    OrderStatusBadge,
    BDropdown,
    BDropdownItem,
    ViewOrderModal,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      bucketUrl,
      avatarText,
      loading: false,
      ticketId: null,
      ticketTypes: [],
      ticket: null,
      rows: [],
      currentUser: null,
      form: {},
      statusOptions: [
        { text: 'Open', value: 'open', variant: 'primary' },
        { text: 'Assigned', value: 'assigned', variant: 'info' },
        { text: 'In Progress', value: 'in_progress', variant: 'warning' },
        { text: 'On Hold', value: 'on_hold', variant: 'secondary' },
        { text: 'Resolved', value: 'resolved', variant: 'success' },
        { text: 'Closed', value: 'closed', variant: 'danger' },
      ],
    }
  },
  computed: {
    currentStatusText() {
      const selectedStatus = this.statusOptions.find(option => option.value === this.ticket.status)
      return selectedStatus ? selectedStatus.text : this.ticket.status
    },
    currentStatusVariant() {
      const selectedStatus = this.statusOptions.find(option => option.value === this.ticket.status)
      return selectedStatus ? `outline-${selectedStatus.variant}` : 'outline-primary'
    },
  },
  created() {
    this.getCurrentUser()
  },
  async mounted() {
    this.ticketId = this.$route.params.id
    this.getTicketData()
    this.getTicketConvos()
  },

  methods: {
    getFileName(url) {
      if (!url) return ''
      return url.split('/').pop() // Gets the last part of the URL (the filename)
    },
    async getCurrentUser() {
      try {
        const data = localStorage.getItem('current_user') ? JSON.parse(localStorage.getItem('current_user')) : (await StaffAuthRepository.getCurrentUser()).data.data
        if (!localStorage.getItem('current_user')) localStorage.setItem('current_user', JSON.stringify(data))
        this.currentUser = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async getTicketData() {
      this.loading = true
      try {
        const { data } = (await TicketRepository.getTicketById(this.ticketId))
        this.ticket = data.data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async getTicketConvos() {
      this.loading = true
      try {
        const { data } = (await TicketRepository.getTicketConvos(this.ticketId))
        this.rows = data.data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async onSubmit() {
      const data = {
        message: this.form.message,
        is_ticket_termination: 0,
      }
      if (this.form.file) data.file = this.form.file
      this.handleSubmit(data)
    },
    async openCloseTicket() {
      // eslint-disable-next-line no-underscore-dangle
      const result = await this.__showSweetAlertReverse('Are You Sure ?', 'Do you want to close this ticket? This action cannot be undone', 'Yes, Close Ticket', 'No, Keep On')
      if (result.isConfirmed) {
        const data = {
          message: 'This ticket has been closed',
          is_ticket_termination: 1,
        }
        await this.handleSubmit(data, true)
        await this.handleTicketClose()
      }
    },
    async handleSubmit(data, openclose) {
      try {
        this.loading = true
        const res = await TicketRepository.updateTicket(this.ticketId, data)
        if (!openclose) {
          if (res.status === 200) {
            if (typeof res.data === 'string') {
              if (res.data.includes('Unknown: file')) {
                res.data = res.data.replace(/<\/?[^>]+(>|$)/g, '')
                res.data = JSON.parse(res.data.replace("Notice:  Unknown: file created in the system's temporary directory in Unknown on line 0", ''))
              }
            }
            this.form = {}
            this.showSuccessMessage('Ticket Updated Successfully')
            this.onClickRefresh()
          } else {
            this.showErrorMessage("Couldn't update ticket")
          }
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async handleTicketClose() {
      try {
        this.loading = true
        const res = await TicketRepository.closeTicket(this.ticketId)
        if (res.status === 200) {
          this.showSuccessMessage('Ticket Closed Successfully')
          this.getTicketData()
          this.onClickRefresh()
        } else {
          this.showErrorMessage('Couldn\'t close ticket')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async handleTicketOpen() {
      try {
        this.loading = true
        const res = await TicketRepository.openTicket(this.ticketId)
        if (res.status === 200) {
          this.showSuccessMessage('Ticket Reopened Successfully')
          this.getTicketData()
          this.onClickRefresh()
        } else {
          this.showErrorMessage('Couldn\'t reopen ticket')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    onClickRefresh() {
      this.getTicketConvos()
    },
    onClickViewOrder(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
    async handleTicketStatus(status) {
      try {
        this.loading = true
        const res = await TicketRepository.updateTicketStatus(this.ticketId, status)
        if (res.status === 200) {
          this.showSuccessMessage('Ticket Status Updated Successfully')
          this.getTicketData()
          this.onClickRefresh()
        } else {
          this.showErrorMessage('Couldn\'t Update Ticket Status')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async updateTicketStatus(status) {
      if (status === 'closed') {
        return
      }
      if (status === this.ticket.status) {
        return
      }
      if (status === 'open') {
        const data = {
          message: 'This ticket has been reopened',
          is_ticket_termination: 0,
        }
        await this.handleSubmit(data, true)
        await this.handleTicketOpen()
      } else {
        const statusName = status.replace(/_/g, ' ')
        const data = {
          message: `This ticket status has been updated as ${statusName}`,
          is_ticket_termination: 0,
        }
        await this.handleSubmit(data, true)
        await this.handleTicketStatus(status, statusName)
      }
      this.ticket.status = status
    },
  },
}
</script>
<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
@import '../@core/scss/vue/libs/vue-flatpicker.scss';
.attachment-section a {
  background-color: #ffffff !important;
}
.attachment-wrapper {
  display: block;
  text-decoration: none !important;

  &:hover {
    .attachment-content {
      /* On hover, make it slightly tinted so the user knows it's clickable */
      background-color: #fcfcff !important;
      border-color: var(--primary);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
    }
  }
}

.attachment-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  /* 1. Set to pure white */
  background-color: #ffffff !important;
  /* 2. Use a lighter, softer dashed border color */
  border: 1px dashed #d8d6de;
  border-radius: 6px;
  /* 3. Add a very subtle shadow to make it feel "lifted" and lighter than the card */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  color: #6e6b7b;
  transition: all 0.2s ease;
}

.attachment-name {
  font-size: 0.9rem;
  color: #5e5873;
}

.attachment-size {
  color: #b9b9c3;
  font-size: 0.85rem;
  margin-left: 5px;
}

.eye-icon {
  color: #82868b;
  flex-shrink: 0;
}

/* Ensure long filenames don't break the layout */
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
}

.ticket-status {
  font-size: 1rem;
  padding: 1.5rem;
}

.ticket-status-badge {
  margin-bottom: 2rem;
}

.new-action-button {
  border: 1px solid #374151;
  color: #374151;
}

.convo-card {
  margin-bottom: 1rem !important;
}

.ticket-convo-status {
  text-align: left !important;
  margin-bottom: 1rem !important;
}

.ticket-convo-status div {
  padding: 0.25rem;
}

.ticket-status-dropdown {
  margin-top: 0.25rem;
}

.ticket-status-dropdown .dropdown-menu {
  overflow: hidden;
  max-height: 200px !important;
}

.ticket-status-dropdown .btn {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.ticket-status-dropdown .dropdown-item {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.2rem 0 0.2rem 0.1rem;
}

.ticket-status-dropdown .dropdown-item.active {
  background-color: #f6f6f6;
}

.ticket-status-dropdown .dropdown-item.active svg {
  margin-left: 0.75rem !important;
  margin-right: 0.75rem !important;
  stroke-width: 5;
}

.ticket-status-dropdown .dropdown-item:hover {
  background-color: transparent;
}

.ticket-status-dropdown .dropdown-item:not(.active):hover {
  transform: translateX(3px); /* Slightly move to the right */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05); /* Add a subtle shadow */
}

.ticket-status-dropdown .dropdown-item .badge {
  width: 100%; /* Adjust as needed */
  text-align: center;
}
</style>
